import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowRight, 
  Check, 
  DollarSign, 
  BarChart2, 
  Clock, 
  AlertTriangle, 
  Music, 
  Info, 
  Users, 
  Globe, 
  Youtube, 
  Image, 
  Headphones, 
  Code, 
  FileText, 
  User, 
  Activity, 
  Shield 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/RoyaltyAdvances.css';

const RoyaltyAdvances = () => {
  const { darkMode } = useTheme();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const renderIcon = (iconName) => {
    const iconSize = 20;
    const iconClass = 'section-icon';
    
    const icons = {
      dollar: <DollarSign size={iconSize} className={iconClass} />,
      info: <Info size={iconSize} className={iconClass} />,
      music: <Music size={iconSize} className={iconClass} />,
      check: <Check size={iconSize} className={iconClass} />,
      users: <Users size={iconSize} className={iconClass} />,
      globe: <Globe size={iconSize} className={iconClass} />,
      youtube: <Youtube size={iconSize} className={iconClass} />,
      image: <Image size={iconSize} className={iconClass} />,
      headphones: <Headphones size={iconSize} className={iconClass} />,
      code: <Code size={iconSize} className={iconClass} />,
      file: <FileText size={iconSize} className={iconClass} />,
      user: <User size={iconSize} className={iconClass} />,
      activity: <Activity size={iconSize} className={iconClass} />,
      shield: <Shield size={iconSize} className={iconClass} />,
    };

    return icons[iconName] || <Info size={iconSize} className={iconClass} />;
  };

  return (
    <div className={`royalty-advances ${darkMode ? 'dark' : ''}`}>
      <section className="hero-section">
        <div className="container">
          <h1>Unlock Your Music's Potential with Royalty Advances</h1>
          <p className="lead">
            Get upfront funding based on your streaming revenue to invest in your music career and accelerate your growth. Read on to learn more about how royalty advances work and if you qualify.
          </p>
        </div>
      </section>

      <section className="how-it-works">
        <div className="container">
          <h2>How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Build Your Catalog</h3>
              <p>Distribute your music through Falphin and grow your streaming numbers.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Earn $1,000+ in 6 Months</h3>
              <p>Once you've established consistent royalty earnings, you may qualify for advances.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Get Funded</h3>
              <p>Receive an advance on your future royalties to invest back into your music.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits">
        <div className="container">
          <h2>Why Consider a Royalty Advance?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <DollarSign size={32} className="benefit-icon" />
              <h3>Upfront Capital</h3>
              <p>Get funds now to cover production, marketing, or other career expenses.</p>
            </div>
            <div className="benefit-card">
              <BarChart2 size={32} className="benefit-icon" />
              <h3>Grow Your Career</h3>
              <p>Invest in high-quality recordings, music videos, or marketing campaigns.</p>
            </div>
            <div className="benefit-card">
              <Clock size={32} className="benefit-icon" />
              <h3>Flexible Repayment</h3>
              <p>Pay back the advance through a small percentage of your future royalties.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="eligibility">
        <div className="container">
          <h2>Eligibility Requirements</h2>
          <div className="requirements">
            <div className="requirement">
              <Check size={24} className="check-icon" />
              <span>Minimum $1,000 in royalties earned through Falphin within 6 months</span>
            </div>
            <div className="requirement">
              <Check size={24} className="check-icon" />
              <span>Consistent monthly streaming activity</span>
            </div>
            <div className="requirement">
              <Check size={24} className="check-icon" />
              <span>Active Falphin account in good standing</span>
            </div>
          </div>
          
          <div className="disclaimer">
            <AlertTriangle size={20} className="alert-icon" />
            <p>
              <strong>Important:</strong> Royalty advances are subject to approval by our funding partners and are not guaranteed. 
              Advance amounts vary based on your streaming performance and revenue consistency. 
              This is not a loan, but an advance on your future royalties.
            </p>
          </div>
        </div>
      </section>

      <section className="potential-advances">
        <div className="container">
          <h2>Potential Advance Amounts</h2>
          <div className="advance-tiers">
            <div className="tier">
              <h3>Starter</h3>
              <div className="amount">$1,000 - $5,000</div>
              <p>For artists earning $1,000+ in 6 months</p>
              <ul>
                <li>Ideal for singles or EPs</li>
                <li>Quick approval process</li>
              </ul>
            </div>
            <div className="tier featured">
              <h3>Professional</h3>
              <div className="amount">$5,000 - $25,000</div>
              <p>For artists with consistent monthly revenue</p>
              <ul>
                <li>Album production funding</li>
                <li>Marketing campaigns</li>
                <li>Music video production</li>
              </ul>
            </div>
            <div className="tier">
              <h3>Premium</h3>
              <div className="amount">$25,000 - $100,000+</div>
              <p>For established artists with significant streams</p>
              <ul>
                <li>Major album rollouts</li>
                <li>Tour support</li>
                <li>Full-scale marketing</li>
              </ul>
            </div>
          </div>
          <p className="note">
            <em>Note:</em> Actual advance amounts are determined based on your specific situation and performance metrics.
            Top-performing artists may qualify for advances exceeding $100,000.
          </p>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Take Your Music to the Next Level?</h2>
          <p>
            If you believe you qualify for a royalty advance, contact our team for more information.
            Our team will review your account and reach out if you're eligible for this opportunity.
          </p>
          <Link to="/contact" className="cta-button primary large">
            Contact Us About Advances
          </Link>
        </div>
      </section>

      <section className="faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-sections">
            {[
              {
                id: 'advance-amount',
                title: 'How is the advance amount determined?',
                icon: 'dollar',
                content: (
                  <div className="section-content">
                    <p>Advance amounts are based on several factors, including:</p>
                    <ul className="faq-list">
                      <li>Your historical streaming performance across all platforms</li>
                      <li>Revenue consistency over the past 6-12 months</li>
                      <li>Growth potential and audience engagement</li>
                      <li>Overall streaming trends and market conditions</li>
                    </ul>
                    <p>Our partners use industry-standard metrics and their proprietary algorithms to evaluate each application and determine an appropriate advance amount.</p>
                  </div>
                )
              },
              {
                id: 'loan-type',
                title: 'Is this a loan?',
                icon: 'info',
                content: (
                  <div className="section-content">
                    <p>No, this is not a traditional loan. Here's how it's different:</p>
                    <ul className="faq-list">
                      <li><strong>No credit check required</strong> - Approval is based on your streaming performance, not your credit score</li>
                      <li><strong>No fixed repayment schedule</strong> - You repay through a percentage of your future royalties</li>
                      <li><strong>No personal liability</strong> - The advance is secured against your future earnings only</li>
                      <li><strong>No hidden fees</strong> - The terms are transparent and straightforward</li>
                    </ul>
                  </div>
                )
              },
              {
                id: 'advance-usage',
                title: 'What can I use the advance for?',
                icon: 'music',
                content: (
                  <div className="section-content">
                    <p>You have full flexibility in how you use your advance. Most artists use it for:</p>
                    <div className="usage-grid">
                      <div className="usage-item">
                        <h4>🎤 Music Production</h4>
                        <p>Studio time, mixing, mastering, and production costs</p>
                      </div>
                      <div className="usage-item">
                        <h4>🎥 Music Videos</h4>
                        <p>Professional video production and visual content</p>
                      </div>
                      <div className="usage-item">
                        <h4>📈 Marketing</h4>
                        <p>Promotions, ads, and social media campaigns</p>
                      </div>
                      <div className="usage-item">
                        <h4>🎵 Tour Support</h4>
                        <p>Travel, accommodation, and production for live shows</p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                id: 'qualification',
                title: 'How do I know if I qualify?',
                icon: 'check',
                content: (
                  <div className="section-content">
                    <h3>Basic Requirements</h3>
                    <ul className="faq-list">
                      <li>Minimum $1,000 in royalties earned through Falphin in the past 6 months</li>
                      <li>Consistent monthly streaming activity</li>
                      <li>Active Falphin account in good standing</li>
                    </ul>
                    
                    <h3>How to Apply</h3>
                    <ol className="faq-list">
                      <li>Log in to your Falphin dashboard</li>
                      <li>Navigate to the 'Royalty Advances' section</li>
                      <li>Complete the application form</li>
                      <li>Our team will review your account and get back to you within 3-5 business days</li>
                    </ol>
                    
                    <div className="cta-box">
                      <p>Not sure if you qualify? <a href="/contact">Contact our team</a> for a free consultation.</p>
                    </div>
                  </div>
                )
              }
            ].map((section) => {
              const isOpen = openSection === section.id;
              return (
                <div 
                  key={section.id} 
                  className={`faq-section ${isOpen ? 'open' : ''}`}
                >
                  <button 
                    className="section-header" 
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={isOpen}
                    aria-controls={`${section.id}-content`}
                  >
                    <div className="section-title">
                      {renderIcon(section.icon)}
                      <h2>{section.title}</h2>
                    </div>
                    <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && (
                    <div 
                      id={`${section.id}-content`}
                      className="section-panel"
                      role="region"
                      aria-labelledby={`${section.id}-header`}
                    >
                      {section.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoyaltyAdvances;
